import { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  Shield, Save, RefreshCw, Users, AlertTriangle, Globe, Clock,
} from 'lucide-react';

/**
 * IpSecurityPage — admin control surface for per-IP signup throttling
 * (iter 128).
 *
 * Two knobs:
 *   • max_per_ip_per_day — how many accounts a single IP may create in 24h
 *   • max_per_ip_total   — lifetime cap per IP
 *
 * Plus a live IP log table so operators can spot farming at a glance.
 */
export default function IpSecurityPage() {
  const [rules, setRules] = useState({
    enabled: true,
    max_per_ip_per_day: 3,
    max_per_ip_total: 10,
    block_message: '',
  });
  const [signups, setSignups] = useState([]);
  const [totalWindow, setTotalWindow] = useState(0);
  const [sinceHours, setSinceHours] = useState(24);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadAll();
  }, []);

  useEffect(() => {
    loadSignups();
  }, [sinceHours]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [rulesRes, signupsRes] = await Promise.all([
        api.get('/api/admin/ip-security/rules'),
        api.get(`/api/admin/ip-security/signups?since_hours=${sinceHours}`),
      ]);
      setRules(prev => ({ ...prev, ...(rulesRes.data?.rules || {}) }));
      setSignups(signupsRes.data?.rows || []);
      setTotalWindow(signupsRes.data?.total_in_window || 0);
    } catch (e) {
      toast.error('Failed to load IP security');
    } finally {
      setLoading(false);
    }
  };

  const loadSignups = async () => {
    setRefreshing(true);
    try {
      const r = await api.get(`/api/admin/ip-security/signups?since_hours=${sinceHours}`);
      setSignups(r.data?.rows || []);
      setTotalWindow(r.data?.total_in_window || 0);
    } catch {
      /* non-fatal */
    } finally {
      setRefreshing(false);
    }
  };

  const saveRules = async () => {
    setSaving(true);
    try {
      await api.put('/api/admin/ip-security/rules', {
        ...rules,
        max_per_ip_per_day: Number(rules.max_per_ip_per_day) || 0,
        max_per_ip_total: Number(rules.max_per_ip_total) || 0,
      });
      toast.success('IP security rules saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center gap-2 text-gray-400" data-testid="ip-security-loading">
        <RefreshCw className="w-4 h-4 animate-spin" /> Loading IP security…
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-5xl" data-testid="admin-ip-security-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="w-6 h-6 text-red-400" />
            IP Security
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            Throttle account creation per IP. Caps apply across every tenant — use this to shut down bot-farming and duplicate-account abuse.
          </p>
        </div>
        <Button
          onClick={saveRules}
          disabled={saving}
          className="bg-[#D4AF37] hover:bg-[#F5D76E] text-black font-bold"
          data-testid="save-ip-rules-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>

      {/* RULES */}
      <div
        className="rounded-2xl p-5 space-y-4 border border-white/5"
        style={{ background: 'rgba(255,255,255,0.02)' }}
        data-testid="ip-rules-section"
      >
        <div className="flex items-start gap-3">
          <Globe className="w-5 h-5 text-red-400 mt-0.5" />
          <div>
            <h2 className="font-bold text-white text-base">Signup Throttle Rules</h2>
            <p className="text-xs text-gray-400 mt-0.5 max-w-3xl leading-relaxed">
              When ON, signup returns HTTP 429 once the cap is hit. Set a field to 0 to disable that specific check.
            </p>
          </div>
        </div>

        <Toggle
          label="Enable IP-based signup throttling"
          value={rules.enabled}
          onChange={v => setRules(p => ({ ...p, enabled: v }))}
          testid="toggle-ip-enabled"
          description="Master switch. When off, no IP checks run on signup."
        />

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-gray-300">Max signups per IP per 24h (0 = unlimited)</label>
            <Input
              type="number"
              min={0}
              value={rules.max_per_ip_per_day}
              onChange={e => setRules(p => ({ ...p, max_per_ip_per_day: parseInt(e.target.value || '0', 10) }))}
              className="bg-[#0B0E14] border-white/10 mt-1.5"
              data-testid="max-per-day-input"
            />
            <p className="text-[10px] text-gray-500 mt-1">
              Rolling 24-hour window. Typical: 3 for friends-and-family fraud control.
            </p>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-300">Max signups per IP lifetime (0 = unlimited)</label>
            <Input
              type="number"
              min={0}
              value={rules.max_per_ip_total}
              onChange={e => setRules(p => ({ ...p, max_per_ip_total: parseInt(e.target.value || '0', 10) }))}
              className="bg-[#0B0E14] border-white/10 mt-1.5"
              data-testid="max-total-input"
            />
            <p className="text-[10px] text-gray-500 mt-1">
              Hard ceiling. Once an IP has created this many accounts total, all future signups from it are blocked.
            </p>
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold text-gray-300">Block message (shown to player on 429)</label>
          <Textarea
            value={rules.block_message}
            onChange={e => setRules(p => ({ ...p, block_message: e.target.value }))}
            rows={2}
            className="bg-[#0B0E14] border-white/10 mt-1.5"
            data-testid="block-message-input"
          />
        </div>
      </div>

      {/* SIGNUP LOG */}
      <div
        className="rounded-2xl p-5 space-y-3 border border-white/5"
        style={{ background: 'rgba(255,255,255,0.02)' }}
        data-testid="ip-signups-section"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <Users className="w-5 h-5 text-amber-400 mt-0.5" />
            <div>
              <h2 className="font-bold text-white text-base">Top Signup IPs</h2>
              <p className="text-xs text-gray-400 mt-0.5">
                Last {sinceHours}h · {totalWindow.toLocaleString()} total signups tracked
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={sinceHours}
              onChange={e => setSinceHours(parseInt(e.target.value, 10))}
              className="bg-[#0B0E14] border border-white/10 rounded-lg px-2 py-1 text-xs text-white"
              data-testid="window-select"
            >
              <option value={1}>Last 1h</option>
              <option value={24}>Last 24h</option>
              <option value={72}>Last 3 days</option>
              <option value={168}>Last 7 days</option>
              <option value={720}>Last 30 days</option>
            </select>
            <Button
              onClick={loadSignups}
              disabled={refreshing}
              size="sm"
              variant="outline"
              className="border-white/10"
              data-testid="refresh-signups-btn"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {signups.length === 0 ? (
          <div className="text-sm text-gray-500 text-center py-8" data-testid="ip-signups-empty">
            No signups logged in this window.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b border-white/10">
                  <th className="text-left py-2 px-2">IP</th>
                  <th className="text-right py-2 px-2">Signups</th>
                  <th className="text-left py-2 px-2">First Seen</th>
                  <th className="text-left py-2 px-2">Last Seen</th>
                  <th className="text-left py-2 px-2">User IDs</th>
                </tr>
              </thead>
              <tbody>
                {signups.map((row) => {
                  const flagged = row.count >= (rules.max_per_ip_per_day || 99999);
                  return (
                    <tr
                      key={row.ip}
                      className="border-b border-white/5 hover:bg-white/5"
                      data-testid={`ip-row-${row.ip}`}
                    >
                      <td className="py-2 px-2 font-mono text-xs text-white flex items-center gap-2">
                        {flagged && <AlertTriangle className="w-3.5 h-3.5 text-red-400" />}
                        {row.ip}
                      </td>
                      <td className={`py-2 px-2 text-right font-bold ${flagged ? 'text-red-400' : 'text-white'}`}>
                        {row.count}
                      </td>
                      <td className="py-2 px-2 text-[11px] text-gray-400">
                        <Clock className="inline w-3 h-3 mr-1" />
                        {row.first_seen ? new Date(row.first_seen).toLocaleString() : '—'}
                      </td>
                      <td className="py-2 px-2 text-[11px] text-gray-400">
                        {row.last_seen ? new Date(row.last_seen).toLocaleString() : '—'}
                      </td>
                      <td className="py-2 px-2 text-[10px] text-gray-500 font-mono truncate max-w-xs">
                        {(row.user_ids || []).slice(0, 3).map(u => u.slice(0, 8)).join(', ')}
                        {row.user_ids && row.user_ids.length > 3 && ` +${row.user_ids.length - 3}`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Toggle({ label, description, value, onChange, testid }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-sm font-semibold text-white">{label}</p>
        {description && <p className="text-xs text-gray-400 mt-0.5 max-w-2xl leading-snug">{description}</p>}
      </div>
      <button
        onClick={() => onChange(!value)}
        className={`w-12 h-6 rounded-full transition-all shrink-0 ${value ? 'bg-red-500' : 'bg-gray-600'}`}
        data-testid={testid}
        aria-label={label}
      >
        <div className={`w-5 h-5 rounded-full bg-white transition-all ${value ? 'translate-x-6' : 'translate-x-0.5'}`} />
      </button>
    </div>
  );
}
